import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, noop } from 'lodash';
import cn from '../../../utils/cn';
import { SPACEBAR_KEY_CODE, RETURN_KEY_CODE } from '../../../utils/Browser/keyCodes';
import { isKeyPressed } from '../../../utils/isKeyPressed';

const Tab = ({
  id = '',
  className = '',
  style = {},
  activeTab = '',
  value,
  label,
  dataTestId = '',
  onChange,
  setContent = noop,
  children = [],
}) => {
  const onKeyPress = useCallback((event) => {
    event.preventDefault();
    
    if (isKeyPressed(event, "Enter", RETURN_KEY_CODE) || isKeyPressed(event, " ", SPACEBAR_KEY_CODE)) {
      onChange(value);
    }
  }, [onChange, value]);

  useEffect(() => {
    if (isEqual(activeTab, value)) {
      setContent(isEmpty(children) ? null : children);
    }
  }, [activeTab, value, children, setContent]);

  const tabClass = cn(
    'cursor-pointer mr-1.5 py-3 px-16',
    {
      'outline focus:outline-blue-500': true,
      'border-t border-brand-blue bg-medium-fill-blue': activeTab === value,
    },
    className
  );

  return (
    <div className="inline-block">
      <button
        id={id}
        className={tabClass}
        style={style}
        onClick={() => onChange(value)}
        role="tab"
        tabIndex={0}
        onKeyPress={onKeyPress}
        aria-current={isEqual(activeTab, value)}
        aria-label={`${label} tab`}
        data-testid={dataTestId}
        aria-selected={activeTab === value}
      >
        <div className="text-display-color text-sm leading-6">
          {label}
        </div>
      </button>
    </div>
  );
};

Tab.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** Override the inline-styles of the root element || Not Recommended */
  style: PropTypes.object,
  /** Sets the text value of the tab item to the string specified. */
  label: PropTypes.string.isRequired,
  /** If value prop passed to Tabs component, this value prop is also required. It assigns a value to the tab so that it can be selected by the Tabs. */
  value: PropTypes.string.isRequired,
  /** Immutable identifier for testing */
  dataTestId: PropTypes.string,
  /** Called when the selected value change. */
  onChange: PropTypes.func.isRequired,
  /** active tab value */
  activeTab: PropTypes.string,
  /** set Content on active Tab */
  setContent: PropTypes.func,
  /** children props for content inside */
  children: PropTypes.node,
};

export default Tab;
